import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import useNotifications from '../../../hooks/useNotifications';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  collapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-3-line', adminOnly: false },
  { id: 'notifications', label: 'Notifications', icon: 'ri-notification-3-line', adminOnly: false },
  { id: 'members', label: 'Members', icon: 'ri-group-2-line', adminOnly: true },
  { id: 'automations', label: 'My Automations', icon: 'ri-flashlight-line', adminOnly: false },
  { id: 'crm', label: 'CRM / Leads', icon: 'ri-contacts-book-2-line', adminOnly: false },
  { id: 'booking', label: 'Book a Demo', icon: 'ri-calendar-check-2-line', adminOnly: false },
  { id: 'settings', label: 'Account Settings', icon: 'ri-settings-3-line', adminOnly: false },
];

const DashboardSidebar = ({ activeSection, onSectionChange, collapsed, onToggle }: SidebarProps) => {
  const { user, profile, signOut, isAdmin } = useAuth();
  const { unreadCount } = useNotifications();

  const displayName = profile
    ? `${profile.first_name} ${profile.last_name}`
    : user?.user_metadata?.first_name
    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim()
    : user?.email?.split('@')[0] || 'Member';

  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const visibleNavItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  return (
    <aside
      className={`flex flex-col h-screen sticky top-0 bg-ea-navy border-r border-ea-dark-border transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 h-16 border-b border-ea-dark-border ${collapsed ? 'justify-center' : ''}`}>
        <Link to="/" className="flex items-center gap-2 cursor-pointer min-w-0">
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <img
              src="https://static.readdy.ai/image/bcf9f2411fadd279f7d1fd38d7e2d7e8/6cfd408f22ccc1f303d260d7c847c128.png"
              alt="EA Logo"
              className="w-8 h-8 object-contain"
            />
          </div>
          {!collapsed && (
            <span className="text-white font-bold text-sm whitespace-nowrap truncate">
              Everything <span className="text-ea-blue">Automation</span>
            </span>
          )}
        </Link>
        <button
          onClick={onToggle}
          className={`ml-auto w-7 h-7 flex items-center justify-center rounded-lg text-ea-gray hover:text-white hover:bg-ea-dark-card transition-colors cursor-pointer shrink-0 ${collapsed ? 'mx-auto ml-auto' : ''}`}
        >
          <i className={`text-sm ${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'}`}></i>
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {isAdmin && (
          <div className={`px-3 py-1 mb-2 ${collapsed ? 'hidden' : ''}`}>
            <span className="text-ea-gray/30 text-[10px] font-semibold uppercase tracking-widest">Admin</span>
          </div>
        )}
        {visibleNavItems.map((item) => {
          const isActive = activeSection === item.id;
          const isNotifications = item.id === 'notifications';
          const showBadge = isNotifications && unreadCount > 0;
          const isAdminItem = item.adminOnly;
          return (
            <div key={item.id}>
              {isAdminItem && !collapsed && item.id === 'members' && (
                <div className="border-t border-ea-dark-border my-2 mx-1"></div>
              )}
              <button
                onClick={() => onSectionChange(item.id)}
                title={collapsed ? item.label : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer text-left group ${
                  isActive
                    ? isAdminItem
                      ? 'bg-orange-500/10 text-white border border-orange-500/20'
                      : 'bg-ea-blue/15 text-white border border-ea-blue/25'
                    : 'text-ea-gray hover:text-white hover:bg-ea-dark-card border border-transparent'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <div className="relative w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base ${isActive ? (isAdminItem ? 'text-orange-400' : 'text-ea-blue') : 'text-ea-gray group-hover:text-white'}`}></i>
                  {showBadge && collapsed && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-ea-blue rounded-full flex items-center justify-center">
                      <span className="text-white text-[8px] font-bold leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    </span>
                  )}
                </div>
                {!collapsed && <span className="text-sm font-medium whitespace-nowrap flex-1">{item.label}</span>}
                {!collapsed && showBadge && (
                  <span className="ml-auto px-1.5 py-0.5 bg-ea-blue rounded-full text-white text-[10px] font-bold leading-none min-w-[18px] text-center shrink-0">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                {!collapsed && isActive && !showBadge && (
                  <div className={`ml-auto w-1.5 h-1.5 rounded-full shrink-0 ${isAdminItem ? 'bg-orange-400' : 'bg-ea-blue'}`}></div>
                )}
              </button>
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="p-3 border-t border-ea-dark-border">
        <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ea-blue to-ea-blue-electric flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold">
              {(profile
                ? `${profile.first_name} ${profile.last_name}`
                : user?.user_metadata?.first_name
                ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim()
                : user?.email?.split('@')[0] || 'M')
                .split(' ')
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </span>
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-white text-xs font-semibold truncate">
                  {profile
                    ? `${profile.first_name} ${profile.last_name}`
                    : user?.user_metadata?.first_name
                    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim()
                    : user?.email?.split('@')[0] || 'Member'}
                </p>
                {isAdmin && (
                  <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-[9px] font-bold rounded-full whitespace-nowrap">ADMIN</span>
                )}
              </div>
              <p className="text-ea-gray/60 text-[10px] truncate">{user?.email}</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={signOut}
              title="Sign out"
              className="w-7 h-7 flex items-center justify-center rounded-lg text-ea-gray hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={signOut}
            title="Sign out"
            className="w-full mt-1 flex items-center justify-center py-1.5 rounded-lg text-ea-gray hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer"
          >
            <i className="ri-logout-box-r-line text-sm"></i>
          </button>
        )}
      </div>
    </aside>
  );
};

export default DashboardSidebar;
